// Hand-written SVG charts for the creator analytics page (no chart library). Rules from the dataviz method:
// - thin marks: columns at most 24 px with a 4 px rounded top, 2 px lines, 8 px dots with a surface ring;
// - hairline solid grid, clean ticks, an axis title with the unit;
// - every value reachable without hovering (a table view under each chart), the same readout on keyboard focus;
// - colours by role from CSS tokens on .viz (style.css), so light and dark each get their own validated steps;
// - a day hidden for privacy is a shaded gap with words in the tooltip and the table, never a zero.
// Text goes in with textContent only (labels can come from the API).

import { gapText, niceTicks, segments, type Point } from "./analytics.ts";
import { h } from "./dom.ts";

const NS = "http://www.w3.org/2000/svg";
function s<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}, text?: string): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  if (text !== undefined) el.textContent = text;
  return el;
}

const PLOT_H = 180;
const X_BAND = 26;
const TOP = 10;
const LEFT = 48;

export interface Series {
  name: string;
  /** css class picking the series colour token: s1, s2 */
  slot: "s1" | "s2";
  points: Point[];
}

interface Common {
  /** what the chart shows, for screen readers ("Players per day") */
  title: string;
  /** the y axis title with its unit ("players", "minutes", "% who came back") */
  unit: string;
  /** a value as text, for ticks, labels and the readout */
  fmt: (v: number) => string;
  /** x labels, one per point */
  xLabel: (i: number) => string;
  minPlayers: number;
  /** a fixed top of the scale (rates: 1) */
  yMax?: number;
}

/** A chart frame that redraws at its own width (so text stays readable on phones) and owns one tooltip. */
function frame(draw: (svg: SVGSVGElement, width: number, tip: Tip) => void, label: string): HTMLElement {
  const tipEl = h("div", { class: "viz-tip", role: "status", hidden: true });
  const box = h("div", { class: "viz-chart" });
  let svg: SVGSVGElement | null = null;
  box.append(tipEl);
  const tip: Tip = {
    show(x: number, y: number, rows: TipRow[], head: string) {
      tipEl.replaceChildren(h("div", { class: "viz-tip-head" }, head), ...rows.map((r) => h("div", { class: "viz-tip-row" }, r.slot ? h("span", { class: `viz-key ${r.slot}` }) : null, h("strong", null, r.value), r.name ? h("span", { class: "viz-tip-name" }, r.name) : null)));
      tipEl.hidden = false;
      const w = box.clientWidth;
      const tw = tipEl.offsetWidth;
      tipEl.style.left = `${Math.max(0, Math.min(w - tw, x + 12 > w - tw ? x - tw - 12 : x + 12))}px`;
      tipEl.style.top = `${Math.max(0, y - 10)}px`;
    },
    hide() {
      tipEl.hidden = true;
    },
  };
  let last = 0;
  // each redraw builds a fresh svg (with its own listeners at its own geometry) and swaps it in
  const redraw = () => {
    if (!box.isConnected && last) return;
    const w = Math.max(280, Math.round(box.clientWidth || 640));
    if (w === last) return;
    last = w;
    const hadFocus = svg !== null && document.activeElement === svg;
    const next = s("svg", { role: "img", "aria-label": label, tabindex: 0, class: "viz-svg", width: w, height: TOP + PLOT_H + X_BAND, viewBox: `0 0 ${w} ${TOP + PLOT_H + X_BAND}` });
    draw(next, w, tip);
    tip.hide();
    if (svg) svg.replaceWith(next);
    else box.prepend(next);
    svg = next;
    if (hadFocus) next.focus();
  };
  if (typeof ResizeObserver !== "undefined") {
    const ro = new ResizeObserver(() => {
      // a chart replaced by a refetch (range change) stops listening
      if (!box.isConnected && last) return ro.disconnect();
      redraw();
    });
    ro.observe(box);
  }
  requestAnimationFrame(redraw);
  return box;
}

interface TipRow {
  value: string;
  name?: string;
  slot?: string;
}
interface Tip {
  show(x: number, y: number, rows: TipRow[], head: string): void;
  hide(): void;
}

/** y scale, gridlines, tick labels and the axis title. Returns value → y. */
function yAxis(svg: SVGSVGElement, width: number, right: number, max: number, c: Common): (v: number) => number {
  const ticks = niceTicks(c.yMax ?? max);
  const top = ticks.at(-1)!;
  const y = (v: number) => TOP + PLOT_H - (v / top) * PLOT_H;
  const g = s("g", { class: "viz-axis", "aria-hidden": "true" });
  for (const t of ticks) {
    g.append(s("line", { x1: LEFT, x2: width - right, y1: y(t), y2: y(t), class: t === 0 ? "viz-base" : "viz-grid" }));
    g.append(s("text", { x: LEFT - 6, y: y(t) + 4, "text-anchor": "end", class: "viz-tick" }, c.fmt(t)));
  }
  g.append(s("text", { x: 0, y: 0, class: "viz-unit", transform: `translate(12 ${TOP + PLOT_H / 2}) rotate(-90)`, "text-anchor": "middle" }, c.unit));
  svg.append(g);
  return y;
}

/** First, middle and last x labels (more only when there's room). */
function xAxis(svg: SVGSVGElement, n: number, xAt: (i: number) => number, c: Common, width: number): void {
  const g = s("g", { class: "viz-axis", "aria-hidden": "true" });
  const room = Math.max(2, Math.floor((width - LEFT) / 70));
  const step = Math.max(1, Math.ceil((n - 1) / Math.max(1, room - 1)));
  const shown = new Set<number>();
  for (let i = 0; i < n; i += step) shown.add(i);
  if (n > 1 && n - 1 - [...shown].at(-1)! < step / 2) shown.delete([...shown].at(-1)!);
  shown.add(n - 1);
  for (const i of shown) {
    if (i < 0) continue;
    const anchor = n === 1 ? "middle" : i === 0 ? "start" : i === n - 1 ? "end" : "middle";
    g.append(s("text", { x: xAt(i) + (anchor === "start" ? -4 : anchor === "end" ? 4 : 0), y: TOP + PLOT_H + 18, "text-anchor": anchor, class: "viz-tick" }, c.xLabel(i)));
  }
  svg.append(g);
}

/** Shaded columns where a day is hidden for privacy (or has no data): a gap, not a zero. */
function gapBands(svg: SVGSVGElement, pts: Point[], band: number, x0: (i: number) => number): void {
  const g = s("g", { "aria-hidden": "true" });
  pts.forEach((p, i) => {
    if (p.gap === "suppressed" || p.gap === "missing") g.append(s("rect", { x: x0(i), y: TOP, width: Math.max(1, band), height: PLOT_H, class: "viz-gap" }));
  });
  svg.append(g);
}

function readout(p: Point, c: Common): string {
  return p.value !== null ? c.fmt(p.value) : gapText(p.gap ?? "missing", c.minPlayers);
}

/** One measure per day as columns. */
export function columnChart(points: Point[], c: Common): HTMLElement {
  return frame((svg, width, tip) => {
    const right = 12;
    const n = Math.max(1, points.length);
    const max = Math.max(0, ...points.map((p) => p.value ?? 0));
    const y = yAxis(svg, width, right, max, c);
    const band = (width - LEFT - right) / n;
    const bar = Math.max(1, Math.min(24, band - 2));
    const x0 = (i: number) => LEFT + i * band;
    const mid = (i: number) => x0(i) + band / 2;
    gapBands(svg, points, band, x0);
    const marks = s("g", { "aria-hidden": "true" });
    points.forEach((p, i) => {
      if (p.value === null) return;
      const top = y(p.value);
      const hgt = TOP + PLOT_H - top;
      if (hgt <= 0) return;
      const x = mid(i) - bar / 2;
      const r = Math.min(4, bar / 2, hgt);
      const base = TOP + PLOT_H;
      marks.append(
        s("path", {
          class: "viz-bar s1",
          "data-i": i,
          d: `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${x + bar - r}Q${x + bar},${top} ${x + bar},${top + r}V${base}Z`,
        }),
      );
    });
    svg.append(marks);
    // the highest day gets its number on the cap; the axis and the table carry the rest
    let peak = -1;
    points.forEach((p, i) => (p.value !== null && (peak < 0 || p.value > points[peak]!.value!) ? (peak = i) : null));
    if (peak >= 0 && points[peak]!.value! > 0) svg.append(s("text", { x: mid(peak), y: y(points[peak]!.value!) - 6, "text-anchor": "middle", class: "viz-label" }, c.fmt(points[peak]!.value!)));
    xAxis(svg, n, mid, c, width);
    hover(svg, n, mid, band, (i) => [{ value: readout(points[i]!, c) }], c, tip, (i) => (points[i]!.value !== null ? y(points[i]!.value!) : TOP + PLOT_H / 2), "bar");
  }, c.title);
}

/** One or two measures per day as lines that break at gaps (a lone day between gaps is a dot). */
export function lineChart(series: Series[], c: Common): HTMLElement {
  return frame((svg, width, tip) => {
    const n = Math.max(1, ...series.map((x) => x.points.length));
    const right = series.length > 1 ? 64 : 16;
    const max = Math.max(0, ...series.flatMap((x) => x.points.map((p) => p.value ?? 0)));
    const y = yAxis(svg, width, right, max, c);
    const band = (width - LEFT - right) / n;
    const x0 = (i: number) => LEFT + i * band;
    const mid = (i: number) => x0(i) + band / 2;
    gapBands(svg, series[0]?.points ?? [], band, x0);
    const marks = s("g", { "aria-hidden": "true" });
    const ends: { y: number; text: string }[] = [];
    for (const sr of series) {
      for (const run of segments(sr.points)) {
        if (run.length === 1) marks.append(s("circle", { cx: mid(run[0]!.i), cy: y(run[0]!.value), r: 4, class: `viz-dot ${sr.slot}` }));
        else marks.append(s("path", { class: `viz-line ${sr.slot}`, d: run.map((p, k) => `${k ? "L" : "M"}${mid(p.i).toFixed(1)},${y(p.value).toFixed(1)}`).join("") }));
      }
      const lastIdx = sr.points.map((p) => p.value !== null).lastIndexOf(true);
      if (lastIdx >= 0) ends.push({ y: y(sr.points[lastIdx]!.value!), text: `${sr.name} ${c.fmt(sr.points[lastIdx]!.value!)}` });
    }
    svg.append(marks);
    // direct end labels only when they don't collide (the legend always names the series)
    if (series.length > 1 && ends.length === series.length && (ends.length < 2 || Math.abs(ends[0]!.y - ends[1]!.y) >= 14))
      for (const e of ends) svg.append(s("text", { x: width - right + 6, y: e.y + 4, class: "viz-label" }, e.text));
    xAxis(svg, n, mid, c, width);
    hover(
      svg,
      n,
      mid,
      band,
      (i) => series.map((sr) => ({ value: readout(sr.points[i] ?? { date: "", value: null, gap: "missing" }, c), name: series.length > 1 ? sr.name : undefined, slot: series.length > 1 ? sr.slot : undefined })),
      c,
      tip,
      (i) => Math.min(...series.map((sr) => (sr.points[i]?.value != null ? y(sr.points[i]!.value!) : TOP + PLOT_H / 2))),
      "line",
      series.map((sr) => ({ slot: sr.slot, y: (i: number) => (sr.points[i]?.value != null ? y(sr.points[i]!.value!) : null) })),
    );
  }, c.title);
}

/** Ordered buckets as columns; a bucket hidden for privacy says so in words instead of a bar. */
export function bucketChart(labels: string[], values: (number | null)[], c: Common): HTMLElement {
  const points: Point[] = values.map((v, i) => ({ date: labels[i] ?? "", value: v, gap: v === null ? "suppressed" : null }));
  return frame((svg, width, tip) => {
    const right = 12;
    const n = Math.max(1, points.length);
    const max = Math.max(0, ...values.map((v) => v ?? 0));
    const y = yAxis(svg, width, right, max, c);
    const band = (width - LEFT - right) / n;
    const bar = Math.max(1, Math.min(24, band - 2));
    const mid = (i: number) => LEFT + i * band + band / 2;
    const marks = s("g");
    points.forEach((p, i) => {
      const base = TOP + PLOT_H;
      if (p.value === null) {
        marks.append(s("text", { x: mid(i), y: base - 8, "text-anchor": "middle", class: "viz-note-text", "aria-hidden": "true" }, `<${c.minPlayers}`));
        return;
      }
      const top = y(p.value);
      const hgt = base - top;
      if (hgt <= 0) return;
      const x = mid(i) - bar / 2;
      const r = Math.min(4, bar / 2, hgt);
      marks.append(s("path", { class: "viz-bar s1", "data-i": i, "aria-hidden": "true", d: `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${x + bar - r}Q${x + bar},${top} ${x + bar},${top + r}V${base}Z` }));
    });
    svg.append(marks);
    let peak = -1;
    points.forEach((p, i) => (p.value !== null && (peak < 0 || p.value > points[peak]!.value!) ? (peak = i) : null));
    if (peak >= 0 && points[peak]!.value! > 0) svg.append(s("text", { x: mid(peak), y: y(points[peak]!.value!) - 6, "text-anchor": "middle", class: "viz-label" }, c.fmt(points[peak]!.value!)));
    // every bucket gets its label when it fits; otherwise every other one
    const g = s("g", { class: "viz-axis", "aria-hidden": "true" });
    const every = band >= 64 ? 1 : 2;
    labels.forEach((l, i) => {
      if (i % every === 0 || i === labels.length - 1) g.append(s("text", { x: mid(i), y: TOP + PLOT_H + 18, "text-anchor": "middle", class: "viz-tick" }, l));
    });
    svg.append(g);
    hover(svg, n, mid, band, (i) => [{ value: points[i]!.value !== null ? c.fmt(points[i]!.value!) : `fewer than ${c.minPlayers} players` }], c, tip, (i) => (points[i]!.value !== null ? y(points[i]!.value!) : TOP + PLOT_H - 20), "bar");
  }, c.title);
}

/**
 * The hover and focus layer. Lines get a crosshair that snaps to the nearest day and dots on each series; columns
 * lift the hovered one. The whole column band is the target. Arrow keys move it when the chart has focus.
 */
function hover(
  svg: SVGSVGElement,
  n: number,
  mid: (i: number) => number,
  band: number,
  rows: (i: number) => TipRow[],
  c: Common,
  tip: Tip,
  yOf: (i: number) => number,
  kind: "bar" | "line",
  lines: { slot: string; y: (i: number) => number | null }[] = [],
): void {
  const layer = s("g", { class: "viz-hover", "aria-hidden": "true" });
  const cross = s("line", { y1: TOP, y2: TOP + PLOT_H, class: "viz-cross", visibility: "hidden" });
  const dots = lines.map((l) => s("circle", { r: 4, class: `viz-dot ${l.slot}`, visibility: "hidden" }));
  layer.append(cross, ...dots);
  svg.append(layer);
  let at = -1;
  const show = (i: number) => {
    at = Math.max(0, Math.min(n - 1, i));
    if (kind === "line") {
      cross.setAttribute("x1", String(mid(at)));
      cross.setAttribute("x2", String(mid(at)));
      cross.setAttribute("visibility", "visible");
      lines.forEach((l, k) => {
        const yy = l.y(at);
        dots[k]!.setAttribute("visibility", yy === null ? "hidden" : "visible");
        if (yy !== null) (dots[k]!.setAttribute("cx", String(mid(at))), dots[k]!.setAttribute("cy", String(yy)));
      });
    } else {
      svg.querySelectorAll(".viz-bar.on").forEach((b) => b.classList.remove("on"));
      svg.querySelector(`.viz-bar[data-i="${at}"]`)?.classList.add("on");
    }
    tip.show(mid(at), yOf(at), rows(at), c.xLabel(at));
  };
  const hide = () => {
    at = -1;
    cross.setAttribute("visibility", "hidden");
    dots.forEach((d) => d.setAttribute("visibility", "hidden"));
    svg.querySelectorAll(".viz-bar.on").forEach((b) => b.classList.remove("on"));
    tip.hide();
  };
  svg.addEventListener("pointermove", (e) => {
    const r = svg.getBoundingClientRect();
    const x = e.clientX - r.left;
    if (x < LEFT - 4) return hide();
    show(Math.floor((x - LEFT) / band));
  });
  svg.addEventListener("pointerleave", hide);
  svg.addEventListener("blur", hide);
  svg.addEventListener("focus", () => show(at < 0 ? n - 1 : at));
  svg.addEventListener("keydown", (e) => {
    const k = (e as KeyboardEvent).key;
    if (k === "ArrowLeft" || k === "ArrowRight" || k === "Home" || k === "End") {
      e.preventDefault();
      show(k === "Home" ? 0 : k === "End" ? n - 1 : (at < 0 ? n - 1 : at) + (k === "ArrowLeft" ? -1 : 1));
    } else if (k === "Escape") hide();
  });
}

/** A legend of line keys (two or more series). */
export function legend(series: Pick<Series, "name" | "slot">[]): HTMLElement {
  return h(
    "ul",
    { class: "viz-legend" },
    series.map((sr) => h("li", null, h("span", { class: `viz-key ${sr.slot}`, "aria-hidden": "true" }), sr.name)),
  );
}

/** The table twin of a chart: every value, gaps in words. */
export function tableView(head: string[], rows: string[][], summary = "Show as a table"): HTMLElement {
  return h(
    "details",
    { class: "viz-table" },
    h("summary", null, summary),
    h(
      "div",
      { class: "table-wrap" },
      h(
        "table",
        null,
        h("thead", null, h("tr", null, head.map((x, i) => h("th", { class: i ? "num" : null }, x)))),
        h("tbody", null, rows.map((r) => h("tr", null, r.map((x, i) => h("td", { class: i ? "num" : null }, x))))),
      ),
    ),
  );
}

// ---- ratio chart (the developer zone's benchmark) ----

export interface RatioRow {
  /** the row's name, as the copy has it */
  label: string;
  /** the ratio against the baseline (1 = the same) */
  ratio: number;
  /** the ratio as the copy writes it ("1.39×"): shown as is, never re-rounded */
  text: string;
  /** the raw values under the label ("16.0 ms against 22.25 ms") */
  detail?: string;
}

export interface RatioOpts {
  /** what the chart shows, for screen readers */
  title: string;
  /** the baseline's name ("Chrome"), drawn on the 1× line */
  baseline: string;
  /** the scale's ends; the default fits every row with a little room */
  domain?: [number, number];
  /** tick step (default 0.1) */
  step?: number;
}

/** The scale a set of ratios needs: 1× always inside, ends on whole steps. */
export function ratioDomain(ratios: number[], step = 0.1): [number, number] {
  const lo = Math.min(1, ...ratios);
  const hi = Math.max(1, ...ratios);
  const f = (v: number) => Math.round(v * 1e6) / 1e6;
  return [f(Math.floor((lo - step / 4) / step) * step), f(Math.ceil((hi + step / 4) / step) * step)];
}

/**
 * Bars from the 1× line: right of it is ahead of the baseline (the lead series, the accent), left of it behind (ink).
 * HTML, not SVG, so the labels wrap on a phone; every value is printed, nothing waits on hover.
 */
export function ratioChart(rows: RatioRow[], o: RatioOpts): HTMLElement {
  const step = o.step ?? 0.1;
  const [lo, hi] = o.domain ?? ratioDomain(rows.map((r) => r.ratio), step);
  const pos = (v: number) => `${(((Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)) * 100).toFixed(3)}%`;
  const ticks: number[] = [];
  for (let v = lo; v <= hi + 1e-9; v += step) ticks.push(Math.round(v * 100) / 100);
  const fmt = (v: number) => `${v.toFixed(v === 1 ? 0 : 1)}×`;
  const grid = () =>
    h(
      "span",
      { class: "ratio-grid", "aria-hidden": "true" },
      ticks.map((t) => h("span", { class: t === 1 ? "ratio-one" : "ratio-tick", style: { left: pos(t) } })),
    );
  return h(
    "figure",
    { class: "ratio", role: "group", "aria-label": o.title },
    h(
      "div",
      { class: "ratio-axis", "aria-hidden": "true" },
      h("span", { class: "ratio-axis-label" }),
      h(
        "span",
        { class: "ratio-axis-track" },
        ticks.map((t) => h("span", { class: `ratio-axis-t${t === 1 ? " one" : ""}`, style: { left: pos(t) } }, t === 1 ? `${o.baseline} ${fmt(t)}` : fmt(t))),
      ),
      h("span", null),
    ),
    h(
      "ol",
      { class: "ratio-rows", role: "list" },
      rows.map((r) => {
        const ahead = r.ratio >= 1;
        const from = pos(Math.min(1, r.ratio));
        const to = pos(Math.max(1, r.ratio));
        return h(
          "li",
          { class: `ratio-row ${ahead ? "ahead" : "behind"}`, "aria-label": `${r.label}: ${r.text}${r.detail ? ` (${r.detail})` : ""}` },
          h("span", { class: "ratio-label", "aria-hidden": "true" }, h("strong", null, r.label), r.detail ? h("span", { class: "ratio-detail" }, r.detail) : null),
          h(
            "span",
            { class: "ratio-track", "aria-hidden": "true" },
            grid(),
            h("span", { class: "ratio-bar", style: { left: from, width: `calc(${to} - ${from})` } }),
          ),
          h("span", { class: "ratio-value", "aria-hidden": "true" }, r.text),
        );
      }),
    ),
  );
}
